/**
 * Highlight Dashboard tokens (Figma DS AIMS OS — node 5029:52424 / component 5029:52477).
 * Metric tile: label + value/unit (left), IconHighlight 40px (right), feedback row.
 */

import { spacingClass } from '../../foundation/spacing.js';

/** Figma `Styte` property values on Highlight Dashboard. */
export const HIGHLIGHT_CARD_VARIANTS = [
  'neutral',
  'primary',
  'green',
  'orange',
  'yellow',
  'purple',
  'lightBlue',
  'limeGreen',
  'red',
];

/** Figma `State` property: Default | Disabled. */
export const HIGHLIGHT_CARD_STATES = ['default', 'disabled'];

export const highlightCardRootClass = [
  'flex w-full min-w-0 flex-col rounded-2xl border-[0.5px] border-solid p-4 text-left',
  spacingClass.gap2x,
].join(' ');

export const highlightCardRootInteractiveClass = [
  'cursor-pointer transition-[filter]',
  'hover:brightness-[0.98] dark:hover:brightness-110',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:ring-offset-2',
].join(' ');

export const highlightCardHeaderRowClass = 'flex w-full items-center justify-between gap-2';

export const highlightCardContentColumnClass = [
  'flex min-w-0 flex-1 flex-col items-start justify-end',
  spacingClass.gap2x,
].join(' ');

/** Body/Regular M — 14px medium, leading 20px */
export const highlightCardLabelClass = 'w-full text-sm font-medium leading-5';

/** Title/Bold M — 20px semibold */
export const highlightCardValueClass = 'shrink-0 whitespace-nowrap text-xl font-semibold leading-none tabular-nums';

export const highlightCardValueRowClass = [
  'flex w-full items-end',
  spacingClass.gap2x,
].join(' ');

/** Body/Regular S — 12px medium, leading 20px */
export const highlightCardUnitClass = 'text-xs font-medium leading-5';

export const highlightCardFeedbackClass = 'w-full text-xs font-medium leading-5';

/** Per-variant surfaces and text (Figma CSS variables → hex fallbacks). */
export const highlightCardVariantClass = {
  neutral: {
    container: 'border-[#bababa] bg-white dark:border-edge dark:bg-surface-muted/80',
    label: 'text-content dark:text-content',
    value: 'text-content',
    unit: 'text-[#5c5c5c] dark:text-content-secondary',
    feedback: 'text-[#5c5c5c] dark:text-content-muted',
  },
  primary: {
    container: 'border-[#2173ff] bg-[#f6f9ff] dark:border-blue-800/60 dark:bg-blue-950/35',
    label: 'text-content dark:text-blue-100',
    value: 'text-[#001740] dark:text-blue-50',
    unit: 'text-[#5c5c5c] dark:text-blue-200/80',
    feedback: 'text-[#5c5c5c] dark:text-blue-200/70',
  },
  green: {
    container: 'border-[#00a07e] bg-[#e5fdf8] dark:border-emerald-800/60 dark:bg-emerald-950/35',
    label: 'text-content dark:text-emerald-100',
    value: 'text-[#003328] dark:text-emerald-50',
    unit: 'text-[#5c5c5c] dark:text-emerald-200/80',
    feedback: 'text-[#5c5c5c] dark:text-emerald-200/70',
  },
  orange: {
    container: 'border-[#ed6c02] bg-[#fff4e5] dark:border-amber-700/60 dark:bg-amber-950/35',
    label: 'text-content dark:text-amber-100',
    value: 'text-[#663c00] dark:text-amber-50',
    unit: 'text-[#5c5c5c] dark:text-amber-200/80',
    feedback: 'text-[#5c5c5c] dark:text-amber-200/70',
  },
  yellow: {
    container: 'border-[#ff9900] bg-[#fffaf0] dark:border-amber-600/60 dark:bg-amber-950/30',
    label: 'text-content dark:text-amber-100',
    value: 'text-[#5c3500] dark:text-amber-50',
    unit: 'text-[#5c5c5c] dark:text-amber-200/80',
    feedback: 'text-[#5c5c5c] dark:text-amber-200/70',
  },
  purple: {
    container: 'border-[#b070f5] bg-[#f3e9fd] dark:border-purple-700/60 dark:bg-purple-950/35',
    label: 'text-content dark:text-purple-100',
    value: 'text-[#2c075c] dark:text-purple-50',
    unit: 'text-[#5c5c5c] dark:text-purple-200/80',
    feedback: 'text-[#5c5c5c] dark:text-purple-200/70',
  },
  lightBlue: {
    container: 'border-[#00b5d9] bg-[#e5f8ff] dark:border-sky-700/60 dark:bg-sky-950/35',
    label: 'text-content dark:text-sky-100',
    value: 'text-[#02445a] dark:text-sky-50',
    unit: 'text-[#5c5c5c] dark:text-sky-200/80',
    feedback: 'text-[#5c5c5c] dark:text-sky-200/70',
  },
  limeGreen: {
    container: 'border-[#a0da1d] bg-[#f9fee5] dark:border-lime-600/60 dark:bg-lime-950/30',
    label: 'text-content dark:text-lime-100',
    value: 'text-[#3e5c0a] dark:text-lime-50',
    unit: 'text-[#5c5c5c] dark:text-lime-200/80',
    feedback: 'text-[#5c5c5c] dark:text-lime-200/70',
  },
  red: {
    container: 'border-[#d32f2f] bg-[#fdeded] dark:border-red-800/60 dark:bg-red-950/35',
    label: 'text-content dark:text-red-100',
    value: 'text-[#5f2120] dark:text-red-50',
    unit: 'text-[#5c5c5c] dark:text-red-200/80',
    feedback: 'text-[#5c5c5c] dark:text-red-200/70',
  },
};

/** Default IconHighlight tokens per Figma `Styte` (5029:52477). Override via `iconHighlight` prop. */
export const highlightCardIconHighlightByVariant = {
  neutral: { bgColor: 'Neutral', iconColor: 'PrimaryAlt' },
  primary: { bgColor: 'Primary', iconColor: 'PrimaryAlt' },
  green: { bgColor: 'Success', iconColor: 'Green' },
  orange: { bgColor: 'Alert', iconColor: 'Alert' },
  yellow: { bgColor: 'Yellow', iconColor: 'Yellow' },
  purple: { bgColor: 'Purple', iconColor: 'Purple' },
  lightBlue: { bgColor: 'LightBlue', iconColor: 'LightBlue' },
  limeGreen: { bgColor: 'LimeGreen', iconColor: 'LimeGreen' },
  red: { bgColor: 'Error', iconColor: 'Error' },
};

/** Figma State=Disabled — shared text + icon surface. */
export const highlightCardDisabledClass = {
  label: 'text-[#5c5c5c] dark:text-content-faint',
  value: 'text-[#5c5c5c] dark:text-content-faint',
  unit: 'text-[#bababa] dark:text-content-faint',
  feedback: 'text-[#bababa] dark:text-content-faint',
};

/** Accepts only documented {@link HIGHLIGHT_CARD_VARIANTS}; unknown values fall back to `neutral`. */
export function normalizeHighlightCardVariant(variant) {
  return HIGHLIGHT_CARD_VARIANTS.includes(variant) ? variant : 'neutral';
}

/** Accepts only documented {@link HIGHLIGHT_CARD_STATES}; `inactive` maps to `disabled`. */
export function normalizeHighlightCardState(state, inactive) {
  if (inactive === true) return 'disabled';
  return HIGHLIGHT_CARD_STATES.includes(state) ? state : 'default';
}

/** Expects normalized variant; merges variant defaults with optional `iconHighlight` overrides. */
export function resolveHighlightCardIconHighlight(variant, iconHighlight = {}) {
  const defaults =
    highlightCardIconHighlightByVariant[variant] ??
    highlightCardIconHighlightByVariant.neutral;

  return {
    size: 'medium',
    ...defaults,
    ...iconHighlight,
  };
}

/** Expects already-normalized variant and state from {@link normalizeHighlightCardVariant} / {@link normalizeHighlightCardState}. */
export function resolveHighlightCardVariantStyles(variant, state) {
  const v = highlightCardVariantClass[variant] ?? highlightCardVariantClass.neutral;

  if (state !== 'disabled') return v;

  return {
    container: v.container,
    label: highlightCardDisabledClass.label,
    value: highlightCardDisabledClass.value,
    unit: highlightCardDisabledClass.unit,
    feedback: highlightCardDisabledClass.feedback,
  };
}
